import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareHeart, Loader2, CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PROVINCE_LIST } from "../../lib/financial-data/canada";
import { analytics } from "../../lib/analytics/ga4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30";

const NEEDS = [
  { value: "retirement", label: "Retirement planning" },
  { value: "investments", label: "Investing my money" },
  { value: "tax", label: "Tax planning" },
  { value: "mortgage", label: "Mortgage / home buying" },
  { value: "debt", label: "Getting out of debt" },
  { value: "budgeting", label: "Budgeting & saving" },
  { value: "estate", label: "Estate / insurance" },
  { value: "business", label: "Business finances" },
  { value: "other", label: "Something else" },
];
const ASSETS = [
  { value: "<25k", label: "Under $25,000" },
  { value: "25k-100k", label: "$25,000 – $100,000" },
  { value: "100k-250k", label: "$100,000 – $250,000" },
  { value: "250k-500k", label: "$250,000 – $500,000" },
  { value: "500k-1m", label: "$500,000 – $1M" },
  { value: "1m+", label: "Over $1M" },
];
const TIMELINES = [
  { value: "now", label: "Right away" },
  { value: "3m", label: "Within 3 months" },
  { value: "6m", label: "Within 6 months" },
  { value: "exploring", label: "Just exploring" },
];

export default function AdvisorCTA() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [form, setForm] = useState({ name: "", email: "", phone: "", need: "retirement", assets: "", timeline: "", province: "", message: "" });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.need) {
      toast.error("Please add your name, a valid email and what you need help with.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/advisor-requests`, { ...form, source: pathname });
      analytics.advisorRequest(pathname, form.need, res.data?.qualified);
      setStatus("done");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  const openForm = () => { setStatus("idle"); setOpen(true); analytics.relatedClick("advisor_cta_open"); };

  return (
    <>
      {/* Floating CTA present on every page */}
      <button
        onClick={openForm}
        data-testid="advisor-cta-button"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-action px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-action-hover"
      >
        <MessageSquareHeart className="h-4 w-4" /> Talk to an advisor
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="advisor-dialog">
          {status === "done" ? (
            <div className="py-6 text-center" data-testid="advisor-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-brand" />
              <h3 className="mt-4 font-heading text-xl font-bold text-foreground">Thanks!</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                One of our advisors will reach out to you shortly.
              </p>
              <button onClick={() => setOpen(false)} className="mt-6 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">Close</button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Get matched with a financial advisor</DialogTitle>
                <DialogDescription>
                  Free, no obligation. Tell us a little about your situation and a licensed Canadian advisor will reach out.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                {/* Primary qualifying question */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-foreground">What do you need help with?</label>
                  <Select value={form.need} onValueChange={(v) => set("need", v)}>
                    <SelectTrigger data-testid="advisor-need" className="bg-white"><SelectValue placeholder="Choose one" /></SelectTrigger>
                    <SelectContent>{NEEDS.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Select value={form.assets} onValueChange={(v) => set("assets", v)}>
                    <SelectTrigger data-testid="advisor-assets" className="bg-white"><SelectValue placeholder="Investable assets" /></SelectTrigger>
                    <SelectContent>{ASSETS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                    <SelectTrigger data-testid="advisor-timeline" className="bg-white"><SelectValue placeholder="How soon?" /></SelectTrigger>
                    <SelectContent>{TIMELINES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input data-testid="advisor-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="advisor-email" type="email" className={field} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input data-testid="advisor-phone" className={field} placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  <Select value={form.province} onValueChange={(v) => set("province", v)}>
                    <SelectTrigger data-testid="advisor-province" className="bg-white"><SelectValue placeholder="Province (optional)" /></SelectTrigger>
                    <SelectContent>{PROVINCE_LIST.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <textarea data-testid="advisor-message" rows={2} className={field} placeholder="Anything else? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} />
                <button type="submit" data-testid="advisor-submit" disabled={status === "loading"} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-action px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-action-hover disabled:opacity-60">
                  {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a free consultation"}
                </button>
                <p className="text-center text-xs text-muted-foreground">We store your details securely and never share your calculator inputs.</p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
